/**
 * Emotional Support & Motivational System - Frontend
 * Provides warm, empathetic messages across the app
 */

import { toast } from 'sonner';

/**
 * Display supportive message with warm styling
 * @param {string} message - The supportive message
 * @param {string} type - Message type: 'success', 'encouragement', 'gentle'
 */
export const showSupportMessage = (message, type = 'success') => {
  const config = {
    duration: 5000,
    style: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '16px 20px',
      fontSize: '15px',
      fontWeight: '500',
      borderRadius: '12px',
      boxShadow: '0 8px 16px rgba(102, 126, 234, 0.25)'
    }
  };

  if (type === 'encouragement') {
    config.style.background = 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    config.duration = 6000;
  } else if (type === 'gentle') {
    config.style.background = 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    config.duration = 7000;
  } else if (type === 'celebration') {
    config.style.background = 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
    config.duration = 5000;
  }

  toast(message, config);
};

/**
 * Display supportive message with actionable suggestion
 * @param {string} message - Main message
 * @param {string} suggestion - Actionable suggestion
 */
export const showSupportWithSuggestion = (message, suggestion) => {
  const combinedMessage = (
    <div>
      <div style={{ marginBottom: '8px', fontWeight: '600' }}>{message}</div>
      {suggestion && (
        <div style={{ fontSize: '14px', opacity: 0.95 }}>💡 {suggestion}</div>
      )}
    </div>
  );

  toast(combinedMessage, {
    duration: 7000,
    style: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '18px 22px',
      borderRadius: '12px',
      boxShadow: '0 8px 16px rgba(102, 126, 234, 0.25)'
    }
  });
};

/**
 * General encouragement messages - Client-side bank
 */
export const ClientSupportMessages = {
  general: [
    "You're doing great — small steps matter. 🌟",
    "Consistency beats perfection. Keep going. 💪",
    "Every choice you make is an investment in your health. ✨",
    "You're showing up for yourself. That's what matters. 🌱"
  ],
  
  gentle: [
    "Let's take it easy today. Your body needs balance. 🍃",
    "Remember to breathe. You're doing just fine. 💙",
    "One step at a time. There's no rush. 🌿",
    "Listen to your body. It knows what it needs. 🌙"
  ],
  
  celebration: [
    "Celebrate this win! You deserve recognition. 🎉",
    "You did it! Every success builds momentum. 🏆",
    "Amazing! You're investing in your best life. ⭐",
    "Well done! Your effort is paying off. 💪"
  ]
};

/**
 * Get random message from category
 * @param {string} category - Message category
 * @returns {string} Random message
 */
export const getRandomSupportMessage = (category = 'general') => {
  const messages = ClientSupportMessages[category] || ClientSupportMessages.general;
  return messages[Math.floor(Math.random() * messages.length)];
};

/**
 * Show random encouragement
 */
export const showRandomEncouragement = () => {
  const message = getRandomSupportMessage('general');
  showSupportMessage(message, 'encouragement');
};

/**
 * Show gentle reminder
 */
export const showGentleReminder = () => {
  const message = getRandomSupportMessage('gentle');
  showSupportMessage(message, 'gentle');
};

/**
 * Show celebration
 */
export const showCelebration = () => {
  const message = getRandomSupportMessage('celebration');
  showSupportMessage(message, 'celebration');
};
